import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getGames, getLiveGames, getPublicGames, LEAGUE_IDS, type Game as ApiGame, type ApiSportsResponse } from "@/lib/apiSports";
import { getCachedData, setCachedData, clearCache, generateCacheKey, CACHE_TTL } from "@/lib/cache";

type League = "NHL" | "AHL" | "ECHL" | "KHL" | "SHL" | "Allsvenskan" | "Liiga" | "Extraliga" | "DEL" | "NL";

export interface TransformedGame {
  id: number;
  homeTeam: string;
  awayTeam: string;
  homeScore: number;
  awayScore: number;
  period: string;
  time: string;
  isLive: boolean;
  homeAbbr: string;
  awayAbbr: string;
  homeLogo: string;
  awayLogo: string;
  tvChannels: string[];
  matchStartUTC: string;
  broadcastStartUTC: string;
  league: League;
  odds: {
    home: number;
    draw: number;
    away: number;
  };
}

// Map API-Sports league IDs to our league names
const LEAGUE_ID_TO_NAME: Record<number, League> = {
  [LEAGUE_IDS.NHL]: "NHL",
  [LEAGUE_IDS.AHL]: "AHL",
  [LEAGUE_IDS.ECHL]: "ECHL",
  [LEAGUE_IDS.KHL]: "KHL",
  [LEAGUE_IDS.SHL]: "SHL",
  [LEAGUE_IDS.ALLSVENSKAN]: "Allsvenskan",
  [LEAGUE_IDS.LIIGA]: "Liiga",
  [LEAGUE_IDS.EXTRALIGA]: "Extraliga",
  [LEAGUE_IDS.DEL]: "DEL",
  [LEAGUE_IDS.NL]: "NL",
};

// Extract abbreviation from team name (first 3 chars of last word as fallback)
function extractAbbreviation(teamName: string): string {
  const words = teamName.split(" ");
  const lastWord = words[words.length - 1];
  return lastWord.substring(0, 3).toUpperCase();
}

// Map game status to period display
function mapStatusToPeriod(status: { long: string; short: string }): string {
  const short = status.short.toUpperCase();
  if (short === "1P" || short === "P1") return "1st";
  if (short === "2P" || short === "P2") return "2nd";
  if (short === "3P" || short === "P3") return "3rd";
  if (short === "OT") return "OT";
  if (short === "BT" || short === "P") return "SO";
  if (short === "FT" || short === "AOT" || short === "AP") return "Final";
  return status.short;
}

// Check if game is currently live
function isGameLive(status: { long: string; short: string }): boolean {
  const liveStatuses = ["1P", "2P", "3P", "P1", "P2", "P3", "OT", "BT", "P", "LIVE", "HT", "INT"];
  return liveStatuses.includes(status.short.toUpperCase());
}

// Generate mock broadcast channels based on league
function getBroadcastChannels(league: League): string[] {
  const broadcasts: Record<League, string[]> = {
    NHL: ["ESPN+", "TNT", "NHL Network"],
    AHL: ["AHL TV", "FloHockey"],
    ECHL: ["FloHockey"],
    KHL: ["KHL TV"],
    SHL: ["C More", "TV4"],
    Allsvenskan: ["C More", "TV4 Play"],
    Liiga: ["Elisa Viihde", "C More"],
    Extraliga: ["O2 TV Sport"],
    DEL: ["MagentaSport"],
    NL: ["MySports"],
  };
  return broadcasts[league] || [];
}

// Generate placeholder odds (API-Sports doesn't provide odds)
function generatePlaceholderOdds() {
  return {
    home: +(1.5 + Math.random() * 1.5).toFixed(2),
    draw: +(3.5 + Math.random() * 1.0).toFixed(2),
    away: +(1.5 + Math.random() * 1.5).toFixed(2),
  };
}

// Check if game is finished
function isGameFinished(status: { long: string; short: string }): boolean {
  const finishedStatuses = ["FT", "AOT", "AP", "AET", "POST"];
  return finishedStatuses.includes(status.short.toUpperCase());
}

// Transform API game to our format
function transformGame(apiGame: ApiGame): TransformedGame {
  const leagueName = LEAGUE_ID_TO_NAME[apiGame.league.id] || "NHL";
  const isLive = isGameLive(apiGame.status);
  const isFinished = isGameFinished(apiGame.status);
  
  // Parse time from game date
  const gameDate = new Date(apiGame.date);
  const matchStartUTC = gameDate.toISOString();
  // Broadcast typically starts 30 mins before
  const broadcastDate = new Date(gameDate.getTime() - 30 * 60 * 1000);
  const broadcastStartUTC = broadcastDate.toISOString();

  // Set period for both live and finished games
  let period = "";
  if (isLive) {
    period = mapStatusToPeriod(apiGame.status);
  } else if (isFinished) {
    period = "Final";
  }

  return {
    id: apiGame.id,
    homeTeam: apiGame.teams.home.name,
    awayTeam: apiGame.teams.away.name,
    homeScore: apiGame.scores.home ?? 0,
    awayScore: apiGame.scores.away ?? 0,
    period,
    time: isLive ? apiGame.time || "" : "",
    isLive,
    homeAbbr: extractAbbreviation(apiGame.teams.home.name),
    awayAbbr: extractAbbreviation(apiGame.teams.away.name),
    homeLogo: apiGame.teams.home.logo,
    awayLogo: apiGame.teams.away.logo,
    tvChannels: getBroadcastChannels(leagueName),
    matchStartUTC,
    broadcastStartUTC,
    league: leagueName,
    odds: generatePlaceholderOdds(),
  };
}

export function useLiveGames() {
  return useQuery({
    queryKey: ["liveGames"],
    queryFn: async () => {
      // Live games use short cache since they update frequently
      const cacheKey = "liveGames";
      const cached = getCachedData<TransformedGame[]>(cacheKey);
      if (cached) return cached;

      const response = await getLiveGames();
      
      if (response.errors?.length > 0) {
        throw new Error(response.errors.join(", "));
      }
      
      const games = response.response.map(transformGame);
      setCachedData(cacheKey, games, CACHE_TTL.LIVE_GAMES);
      return games;
    },
    staleTime: 30 * 1000, // Refresh every 30 seconds for live data
    refetchInterval: 30 * 1000,
    retry: 2,
  });
}

export function useTodaysGames(league?: League) {
  const today = new Date().toISOString().split("T")[0];
  const leagueId = league ? LEAGUE_IDS[league.toUpperCase() as keyof typeof LEAGUE_IDS] : undefined;
  
  // Hockey seasons span two years (e.g., 2025-2026). Use the starting year.
  const now = new Date();
  const month = now.getMonth(); // 0-indexed, Jan=0
  const seasonYear = month >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  
  return useQuery({
    queryKey: ["todaysGames", today, leagueId],
    queryFn: async () => {
      const cacheKey = generateCacheKey("todaysGames", { date: today, league: leagueId || "all" });
      const cached = getCachedData<TransformedGame[]>(cacheKey);
      if (cached) return cached;

      const params: { date: string; league?: number; season?: number } = { date: today };
      if (leagueId) {
        params.league = leagueId;
        params.season = seasonYear;
      }
      
      const response = await getGames(params);
      
      if (response.errors?.length > 0) {
        throw new Error(response.errors.join(", "));
      }
      
      const games = response.response.map(transformGame);
      setCachedData(cacheKey, games, CACHE_TTL.LIVE_GAMES);
      return games;
    },
    staleTime: 60 * 1000,
    retry: 2,
  });
}

// Helper to delay execution
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper to fetch with retry and exponential backoff
async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Check if it's a rate limit error
      if (lastError.message.includes('429') || lastError.message.includes('rate limit')) {
        // Wait longer for rate limits
        const waitTime = baseDelay * Math.pow(2, attempt) * 2;
        console.log(`Rate limited, waiting ${waitTime}ms before retry ${attempt + 1}/${maxRetries}`);
        await delay(waitTime);
      } else if (attempt < maxRetries - 1) {
        // Regular exponential backoff for other errors
        const waitTime = baseDelay * Math.pow(2, attempt);
        await delay(waitTime);
      }
    }
  }
  
  throw lastError;
}

// Only the 10 leagues displayed in the Scores UI
const DISPLAYED_LEAGUE_IDS = [
  LEAGUE_IDS.NHL,
  LEAGUE_IDS.AHL,
  LEAGUE_IDS.ECHL,
  LEAGUE_IDS.KHL,
  LEAGUE_IDS.SHL,
  LEAGUE_IDS.ALLSVENSKAN,
  LEAGUE_IDS.LIIGA,
  LEAGUE_IDS.EXTRALIGA,
  LEAGUE_IDS.DEL,
  LEAGUE_IDS.NL,
];

export function useAllTodaysGames() {
  const today = new Date().toISOString().split("T")[0];
  const queryClient = useQueryClient();
  
  // Hockey seasons span two years (e.g., 2025-2026). Use the starting year.
  const now = new Date();
  const month = now.getMonth(); // 0-indexed, Jan=0
  const seasonYear = month >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  
  const query = useQuery({
    queryKey: ["allTodaysGames", today],
    queryFn: async () => {
      console.log("Fetching fresh games data from API for", today);
      const allGames: TransformedGame[] = [];
      
      // Batch leagues into groups of 3 to reduce parallel load
      const batchSize = 3;
      const batches: number[][] = [];
      
      for (let i = 0; i < DISPLAYED_LEAGUE_IDS.length; i += batchSize) {
        batches.push(DISPLAYED_LEAGUE_IDS.slice(i, i + batchSize));
      }
      
      for (const batch of batches) {
        const results = await Promise.allSettled(
          batch.map(leagueId => 
            fetchWithRetry<ApiSportsResponse<ApiGame[]>>(
              () => getPublicGames({ date: today, league: leagueId, season: seasonYear }),
              3,
              500
            )
          )
        );
        
        for (const result of results) {
          if (result.status === "fulfilled" && result.value.response) {
            const transformed = result.value.response.map(transformGame);
            allGames.push(...transformed);
          } else if (result.status === "rejected") {
            console.warn("Failed to fetch games for batch:", result.reason);
          }
        }
        
        // Small delay between batches to avoid rate limits
        if (batches.indexOf(batch) < batches.length - 1) {
          await delay(200);
        }
      }

      console.log(`Fetched ${allGames.length} total games across all leagues`);
      
      // Sort: live games first, then by start time
      const sortedGames = allGames.sort((a, b) => {
        if (a.isLive && !b.isLive) return -1;
        if (!a.isLive && b.isLive) return 1;
        return new Date(a.matchStartUTC).getTime() - new Date(b.matchStartUTC).getTime();
      });

      return sortedGames;
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    retry: 1,
    retryDelay: 5000,
  });

  // Force refresh that fully invalidates the query cache
  const forceRefresh = async () => {
    clearCache('allTodaysGames');
    await queryClient.invalidateQueries({ queryKey: ["allTodaysGames"] });
  };

  return { ...query, forceRefresh };
}
